<!-- Floating trigger button -->
<button class="chatbot-fab" (click)="toggle()" [attr.aria-label]="isOpen ? 'Close assistant' : 'Open PropDesk assistant'">
  <mat-icon>{{ isOpen ? 'close' : 'chat' }}</mat-icon>
</button>

<!-- Chat panel -->
<div class="chatbot-panel"
     [class.chatbot-panel--open]="isOpen"
     [class.chatbot-panel--maximized]="isMaximized"
     role="dialog" aria-label="PropDesk Assistant">

  <div class="chatbot-panel__header">
    <div class="chatbot-panel__header-info">
      <mat-icon class="chatbot-panel__header-icon">query_stats</mat-icon>
      <span class="chatbot-panel__title">PropDesk Assistant</span>
    </div>
    <div class="chatbot-panel__header-actions">
      <button class="chatbot-panel__close" (click)="toggleMaximize()" [attr.aria-label]="isMaximized ? 'Restore' : 'Maximize'">
        <mat-icon>{{ isMaximized ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
      </button>
      <button class="chatbot-panel__close" (click)="toggle()" aria-label="Close assistant">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <div class="chatbot-panel__messages" #messagesContainer>
    <div
      *ngFor="let msg of messages"
      class="chatbot-msg"
      [class.chatbot-msg--bot]="msg.from === 'bot'"
      [class.chatbot-msg--user]="msg.from === 'user'">
      <ng-container *ngIf="msg.table; else plainBubble">
        <div class="chatbot-msg__bubble chatbot-msg__bubble--table">
          <div class="chatbot-table__title">{{ msg.table.title }}</div>
          <div *ngFor="let section of msg.table.sections" class="chatbot-table__section">
            <div class="chatbot-table__section-name">{{ section.name }} ({{ section.rows.length }})</div>
            <table class="chatbot-table">
              <thead>
                <tr>
                  <th *ngFor="let h of msg.table.headers">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of section.rows">
                  <td *ngFor="let cell of row">{{ cell }}</td>
                </tr>
                <tr *ngIf="section.rows.length === 0">
                  <td [attr.colspan]="msg.table.headers.length">No data found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </ng-container>
      <ng-template #plainBubble>
        <span class="chatbot-msg__bubble"
              [class.chatbot-msg__bubble--note]="msg.note"
              [class.chatbot-msg__bubble--intro]="msg.intro">{{ msg.text }}</span>
      </ng-template>
    </div>

    <div *ngIf="isLoading" class="chatbot-msg chatbot-msg--bot">
      <span class="chatbot-msg__bubble chatbot-msg__bubble--loading">
        <span></span><span></span><span></span>
      </span>
    </div>
  </div>

  <div class="chatbot-panel__footer">

    <!-- Not signed in -->
    <ng-container *ngIf="!auth.isMember">
      <p class="chatbot-panel__auth-hint">
        <a routerLink="/login">Sign in</a> to access range data.
      </p>
    </ng-container>

    <!-- Step 1: Pick ticker -->
    <ng-container *ngIf="auth.isMember && step === 'ticker'">
      <p class="chatbot-panel__section-label">Select ticker</p>
      <div class="chatbot-panel__chips">
        <button
          *ngFor="let t of tickers"
          class="chatbot-chip chatbot-chip--levels"
          [disabled]="isLoading"
          (click)="selectTicker(t)">
          {{ t }}
        </button>
      </div>
    </ng-container>

    <!-- Step 2: Pick time period -->
    <ng-container *ngIf="auth.isMember && step === 'time'">
      <p class="chatbot-panel__section-label">{{ selectedTicker }} — select</p>
      <div class="chatbot-panel__chips">
        <button
          *ngFor="let opt of timeOptions"
          class="chatbot-chip chatbot-chip--levels"
          [disabled]="isLoading || econLoading"
          (click)="onTimeSelect(opt.id)">
          {{ opt.label }}
        </button>
        <button class="chatbot-chip chatbot-chip--back" (click)="backToTickers()">
          ← Back
        </button>
      </div>
    </ng-container>

    <!-- Step 3a: Pick holiday names (multi-select) -->
    <ng-container *ngIf="auth.isMember && step === 'holiday'">
      <p class="chatbot-panel__section-label">Select holiday (multi-select)</p>
      <div *ngIf="holidayLoading" class="chatbot-econ-loading">Loading holidays…</div>
      <div *ngIf="!holidayLoading && uniqueHolidayNames.length === 0" class="chatbot-econ-loading">No holidays found.</div>
      <div *ngIf="!holidayLoading && uniqueHolidayNames.length > 0" class="chatbot-panel__chips">
        <button
          class="chatbot-chip"
          [class.chatbot-chip--selected]="selectedHolidayNames.size === uniqueHolidayNames.length"
          [class.chatbot-chip--levels]="selectedHolidayNames.size !== uniqueHolidayNames.length"
          (click)="toggleAllHolidays()">
          All
        </button>
        <button
          *ngFor="let name of uniqueHolidayNames"
          class="chatbot-chip"
          [class.chatbot-chip--selected]="selectedHolidayNames.has(name)"
          [class.chatbot-chip--levels]="!selectedHolidayNames.has(name)"
          (click)="toggleHolidayName(name)">
          {{ name }}
        </button>
      </div>
      <div *ngIf="!holidayLoading" class="chatbot-econ-actions">
        <button class="chatbot-chip chatbot-chip--levels"
                [disabled]="selectedHolidayNames.size === 0"
                (click)="submitHolidayNames()">
          Next →
        </button>
        <button class="chatbot-chip chatbot-chip--back" (click)="backToTime()">← Back</button>
      </div>
    </ng-container>

    <!-- Step 3b: Pick period(s) -->
    <ng-container *ngIf="auth.isMember && step === 'holiday-period'">
      <p class="chatbot-panel__section-label">Select period (multi-select)</p>
      <div class="chatbot-panel__chips">
        <button
          *ngFor="let p of holidayPeriodOptions"
          class="chatbot-chip"
          [class.chatbot-chip--selected]="selectedHolidayPeriods.has(p.id)"
          [class.chatbot-chip--levels]="!selectedHolidayPeriods.has(p.id)"
          (click)="toggleHolidayPeriod(p.id)">
          {{ p.label }}
        </button>
      </div>
      <div class="chatbot-econ-actions">
        <button class="chatbot-chip chatbot-chip--levels"
                [disabled]="selectedHolidayPeriods.size === 0"
                (click)="submitHolidayPeriods()">
          Show Ranges
        </button>
        <button class="chatbot-chip chatbot-chip--back" (click)="step = 'holiday'">← Back</button>
      </div>
    </ng-container>

    <!-- Step 3: Pick economic events -->
    <ng-container *ngIf="auth.isMember && step === 'econ'">
      <p class="chatbot-panel__section-label">Select events (multi-select)</p>
      <div *ngIf="econLoading" class="chatbot-econ-loading">Loading events…</div>
      <div *ngIf="!econLoading && econEventNames.length === 0" class="chatbot-econ-loading">
        No events found. Ask an admin to open the Economic Calendar page to generate the index.
      </div>
      <div *ngIf="!econLoading && econEventNames.length > 0" class="chatbot-panel__chips">
        <button
          *ngFor="let name of econEventNames"
          class="chatbot-chip"
          [class.chatbot-chip--levels]="!selectedEconEvents.has(name)"
          [class.chatbot-chip--selected]="selectedEconEvents.has(name)"
          (click)="toggleEconEvent(name)">
          {{ name }}
        </button>
      </div>
      <div *ngIf="!econLoading" class="chatbot-econ-actions">
        <button
          class="chatbot-chip chatbot-chip--levels"
          [disabled]="selectedEconEvents.size === 0"
          (click)="submitEconEvents()">
          Show Ranges
        </button>
        <button class="chatbot-chip chatbot-chip--back" (click)="backToTime()">
          ← Back
        </button>
      </div>
    </ng-container>

  </div>

</div>
